import {
  Box,
  Chip,
  Fab,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'src/app/hooks';
import BottomHeader from './Bottom';
import ObjectPreview from 'src/components/ObjectPreview';
import CustomDashboardObject from 'src/components/CustomDashboardObject';
import ModalManageObjectByGroup from 'src/components/ModalManageObjectByGroup';
import TooltipCustomize from 'src/components/TooltipCustomize';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router';
import { CUSTOM_DASHBOARD } from 'src/route';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect } from 'react';
import ModalManageObjectRetail from 'src/components/Retail/ModalManageObjectRetail';
import ObjectPreviewRetail from 'src/components/Retail/ObjectPreviewRetail';
import DateRangeLabel from '../components/DateRangeLabel';
import SelectDateRange from 'src/components/SelectDateRange';
import ModalSelectWidget from 'src/components/ModalSelectWidget';

interface HeaderWrapperProps {
  isOnTour: boolean;
}

const HeaderWrapper = styled(Box)<HeaderWrapperProps>(
  ({ theme, isOnTour }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    ${!isOnTour && 'z-index: 5;'}
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

function HeaderCustomDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const timezone = useAppSelector((state) => state.users.project.timezone);
  const { objectListByObjectType } = useAppSelector(
    (state) => state.objectManagement
  );
  const selectedDashboard = useAppSelector(
    (state) => state.storeCustomDashboard.dashboardSelectedById
  );
  const { isOnTour } = useAppSelector((state) => state.storeProductTour);

  const handleBack = () => {
    navigate(CUSTOM_DASHBOARD);
  };

  const matchedMediaUpMd = useMediaQuery(theme.breakpoints.up('md'));

  // back to dashboard list if selectedDashboard is null
  useEffect(() => {
    if (!selectedDashboard) handleBack();
  }, [selectedDashboard]);

  if (!selectedDashboard) return null;

  const menuName = timezone === 7 ? 'Custom Dashboard' : 'Instant Insight';

  return (
    <HeaderWrapper display="flex" alignItems="center" isOnTour={isOnTour}>
      <Grid container gap={1}>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center">
            <Fab
              sx={{
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[200]}`,
                mr: theme.spacing(2)
              }}
              size="small"
              onClick={handleBack}
            >
              <ArrowBackIcon />
            </Fab>
            <Typography
              variant="h1"
              sx={{
                mr: 1
              }}
            >
              {menuName}
            </Typography>
            <TooltipCustomize
              title={'Personalize your own Dashboard with customizable metrics'}
              placement="top-start"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ transform: 'translateY(-4px)' }}
              />
            </TooltipCustomize>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Box mt={2} width={'100%'}>
            <CustomDashboardObject />
          </Box>
        </Grid>
        <Grid item xs={12}>
          {selectedDashboard.sourceType === 'socmed' ? (
            <Stack
              direction={matchesUpSm ? 'row' : 'column'}
              alignItems={matchesUpSm ? 'center' : 'start'}
              spacing={1}
              my={1.5}
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                {selectedDashboard.objectGroupList.length > 0 ? (
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    sx={{
                      border: '1px solid #E0E0E0',
                      padding: '6px 10px',
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      width: '20%'
                    }}
                  >
                    {selectedDashboard.objectGroupList.map((group) => (
                      <Chip
                        key={group.id}
                        label={group.displayName}
                        variant="outlined"
                        sx={{
                          backgroundColor: '#ffffffff',
                          color: '#4B4B4B',
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <ModalManageObjectRetail
                    title={menuName}
                    isDashboard={true}
                  />
                )}
                <ObjectPreviewRetail
                  objectList={
                    selectedDashboard.objectGroupList.length > 0
                      ? selectedDashboard.objectGroupList
                          .map((group) => group.objectList)
                          .flat()
                      : objectListByObjectType
                  }
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <SelectDateRange />
                {matchedMediaUpMd && (
                  <ModalSelectWidget
                    prevSelectedWidget={
                      selectedDashboard?.customDashboardWidgetMetadataList ?? []
                    }
                  />
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack>
              <span>
                <Chip
                  variant="outlined"
                  label={`Clipping: ${
                    selectedDashboard?.clippingOnm?.name ||
                    selectedDashboard?.clippingOfm?.name
                  }`}
                  sx={{
                    backgroundColor: '#ffffffff',
                    color: '#4B4B4B',
                    fontWeight: 500,
                    borderRadius: 1
                  }}
                />
              </span>
            </Stack>
          )}
        </Grid>
        <Grid item xs={12} mb={2}>
          <DateRangeLabel label={`Viewing data within`} />
        </Grid>
      </Grid>
    </HeaderWrapper>
  );
}

export default HeaderCustomDashboard;
